import * as React from 'react'
import { HybridSettingsPageResourceContainer } from './HybridSettingsPageResourceContainer'
import { SupportedRuntimesTooltipResolver } from '../../../../util/SupportedRuntimesHelper'
import { HybridSettingsPageResourceTooltip } from './HybridSettingsPageResourceTooltip'
import { ResourceSelectorContainer } from '../../../ResourceSelector/model'
import { Environment } from '../../../../remote-api/CoreServicesRemoteApi'
import { ARMServer } from '../../../../remote-api/models'
import Select from '@mulesoft/anypoint-components/lib/Select'
import Form from '@mulesoft/anypoint-components/lib/Form'
import { DownloadAmInstruction } from './DownloadAmInstruction'
import './HybridSettingsPage.css'

export type ValueProps = {
  organizationId: string,
  isLoading: boolean,
  servers: ARMServer[],
  serversLoading: boolean,
  selectedServerId?: string,
  selectedServerGroupId?: string,
  environments: Environment[],
  environmentsLoading: boolean,
  selectedEnvironment?: string,
  applyingPatchSettingsAction: boolean,
  publicUrl: string
}

export type EventProps = {
  initializeLoading: () => void
  downloadedInstaller: () => void
  fetchEnvironments: (resourceSelectorContainer: ResourceSelectorContainer) => void
  selectEnvironment: (resourceSelectorContainer: ResourceSelectorContainer) => void
  fetchServers: (orgId: string, envId: string) => void
  selectServer: (serverId?: string) => void
  selectServerGroup: (serverId?: string) => void
  fetchPublicUrl: () => void
  changeLocalStorageSettingsEnv: (environmentId: string) => void
}

export type Props = ValueProps & EventProps

export class HybridSettingsPage extends React.Component<Props> {
  groupTargetTypes = ['SERVER_GROUP', 'CLUSTER']

  static getResolvedEnvironments(environments: Environment[]) {
    if (!environments) return [{value: '', label: 'Loading...'}]

    return environments.map(env => {
      return {value: env.id, label: env.name}
    })
  }

  static getResolvedServers(servers: ARMServer[]) {
    if (!servers) return [{value: '', label: 'Loading...'}]

    return servers.map(server => {
      return {value: server.id, label: server.name, type: server.type}
    })
  }

  static getEnvironmentFromUrl() {
    const urlArray = window.location.hash.split('/')
    const urlLastElement = urlArray[urlArray.length - 1]
    return urlLastElement !== 'hybrid' ? urlLastElement : undefined
  }

  static renderedResource(option: any) {
    const isLongName = option.label.length > 40
    if (isLongName) {
      return (<HybridSettingsPageResourceTooltip option={option}/>)
    }

    return (<HybridSettingsPageResourceContainer option={option} ellipsis="truncate-ellipsis-hybrid"/>)
  }

  constructor(props: Props) {
    super(props)

    this.handleFormChange = this.handleFormChange.bind(this)
    this.downloadInstaller = this.downloadInstaller.bind(this)
  }

  findServer(serverId?: String) {
    return this.props.servers.find(s => `${s.id}` === serverId)
  }

  isGroup(serverType: String) {
    const group: String | undefined = this.groupTargetTypes.find(g => `${g}` === serverType)
    return group && group.length > 0
  }

  getResolvedServerFromGroup(serverSelected?: ARMServer) {
    if (serverSelected && this.isGroup(serverSelected.type) && serverSelected.details.servers) {
      return serverSelected.details.servers.map(server => {
        return {value: server.id, label: server.name, type: server.type}
      })

    }
    return []
  }

  downloadInstaller() {
    window.open(this.props.publicUrl, '_blank')
    this.props.downloadedInstaller()
  }

  componentDidMount() {
    const {organizationId, selectedEnvironment, environments} = this.props

    this.props.initializeLoading()
    this.props.fetchPublicUrl()

    const resourceSelectorContainer = {
      orgId: organizationId,
      isLogFiles: true
    }

    if (!selectedEnvironment || !environments) this.props.fetchEnvironments(resourceSelectorContainer)

    const envFromURL = HybridSettingsPage.getEnvironmentFromUrl()
    if (envFromURL && !selectedEnvironment) {
      const resourceSelectorContainerWithEnv = {...resourceSelectorContainer, envId: envFromURL}

      this.props.selectEnvironment(resourceSelectorContainerWithEnv)
      this.props.selectServer(undefined)
      this.props.selectServerGroup(undefined)
    } else if (selectedEnvironment && !envFromURL) {
      window.location.hash = `#/settings/hybrid/environments/${selectedEnvironment}`
      this.props.fetchServers(organizationId, selectedEnvironment)
    }
  }

  handleFormChange(form: any) {
    if (form.target === 'environment') {
      this.props.initializeLoading()
      const resourceSelectorContainer = {
        orgId: this.props.organizationId,
        envId: form.value.environment,
        isLogFiles: true
      }
      this.props.selectEnvironment(resourceSelectorContainer)
      this.props.selectServer(undefined)
      this.props.selectServerGroup(undefined)
      this.props.fetchServers(this.props.organizationId, form.value.environment)
      this.props.changeLocalStorageSettingsEnv(form.value.environment)
    } else if (form.target === 'server') {
      this.props.selectServer(form.value.server)
    } else if (form.target === 'serverOrServerGroup') {
      if (this.serverIsGroup(form.value.serverOrServerGroup)) {
        this.props.selectServerGroup(form.value.serverOrServerGroup)
        this.props.selectServer(undefined)
      } else {
        this.props.selectServerGroup(undefined)
        this.props.selectServer(form.value.serverOrServerGroup)
      }
    }
  }

  renderServerFromGroup(servers: { value: String, label: String }[]) {
    return this.serverIsGroup(this.props.selectedServerGroupId) && (
      <div className={'server-select-input'}>
        <div data-test-id={'server-label'} className={'select-label'}>Server</div>
        <Select
          className={'server-group'}
          testId={'server-select'}
          name="server"
          options={servers}
          isLoading={this.props.serversLoading}
          disabled={!this.props.selectedEnvironment}
          placeholder={servers.length > 0 ? 'Select server' : 'No servers found...'}
        />
      </div>
    )
  }

  private serverIsGroup(serverId?: string) {
    const serverSelected = this.findServer(serverId)
    return (serverSelected && this.isGroup(serverSelected.type))
  }

  render() {
    const {servers, serversLoading, selectedServerId, selectedServerGroupId, environments, environmentsLoading, selectedEnvironment} = this.props

    const values = {
      server: selectedServerId,
      serverOrServerGroup: selectedServerGroupId ? selectedServerGroupId : selectedServerId,
      environment: selectedEnvironment
    }

    const resolvedEnvironments = HybridSettingsPage.getResolvedEnvironments(environments)
    const resolvedServers = HybridSettingsPage.getResolvedServers(servers)
    const resolvedServerByGroup = this.getResolvedServerFromGroup(this.findServer(selectedServerGroupId))

    if (environments.length > 0) {
      const envFromURL = HybridSettingsPage.getEnvironmentFromUrl()
      if (selectedEnvironment && !envFromURL) {
        window.location.hash = `#/settings/hybrid/environments/${selectedEnvironment}`
      } else if (!selectedEnvironment && envFromURL && environments.find(env => env.id === envFromURL)) {
        this.handleFormChange({target: 'environment', value: {environment: envFromURL}})
      } else if (!selectedEnvironment && envFromURL && !environments.find(env => env.id === envFromURL)) {
        window.location.hash = `#/settings/hybrid/`
      }
    }

    return (
      <div className={'hybrid-settings-page-container'} data-test-id="hybrid-settings-page-container">
        <div className={'hybrid-settings-page-header'} data-test-id="hybrid-settings-page-header">
          <div className={'hybrid-settings-page-header-title'} data-test-id="hybrid-settings-page-header-title">
            <span>Configure Anypoint Monitoring for On-Premise Environments</span>
          </div>
          <div className={'hybrid-settings-page-header-subtitle'} data-test-id="hybrid-settings-page-header-subtitle">
            <span className={'hybrid-settings-page-header-subtitle-description'} data-test-id="hybrid-settings-page-header-subtitle-description">
              Monitor, alert, and troubleshoot your On-Premise Servers, Mule Runtimes, API Gateways, and APIs using Anypoint Monitoring.
            </span>
            <div
              className={'hybrid-settings-page-header-subtitle-versions-and-link'}
              data-test-id="hybrid-settings-page-header-subtitle-versions-and-link"
            >
              <a href={'https://docs.mulesoft.com/monitoring/am-installing'} target={'_blank'}>Visit the documentation</a> to learn more.
            </div>
          </div>
        </div>
        <div className={'hybrid-settings-body-container'} data-test-id="hybrid-settings-body-container">
          <div className={'hybrid-settings-downloaders-container'} data-test-id="hybrid-settings-downloaders-container">
            <div className={'hybrid-settings-downloaders'} data-test-id="hybrid-settings-downloaders">

              <div className={'hybrid-settings-configure-server'} data-test-id="hybrid-settings-configure-server">
                <span>1. Select the server you want to deploy Anypoint Monitoring:</span>
                <Form
                  className={'hybrid-settings-selector-form'}
                  testId={'selector-form'}
                  onChange={this.handleFormChange}
                  value={values}
                >
                  <div className={'environment-select-input'} data-test-id="environment-select-input">
                    <div data-test-id={'environment-label'} className={'select-label'}>Environment</div>
                    <Select
                      className={'hybrid-environment-select'}
                      testId={'environment-select'}
                      name="environment"
                      options={resolvedEnvironments}
                      isLoading={environmentsLoading}
                      placeholder={resolvedEnvironments.length > 0 ? 'Select environment' : 'No environments found...'}
                    />
                  </div>
                  <div className={'server-select-input'} data-test-id="server-select-input">
                    <div data-test-id={'server-label'} className={'select-label'}>Server / Server Group / Cluster</div>
                    <Select
                      optionRenderer={HybridSettingsPage.renderedResource}
                      valueRenderer={HybridSettingsPage.renderedResource}
                      className={'hybrid-server-select'}
                      testId={'server-or-server-group-select'}
                      name="serverOrServerGroup"
                      options={resolvedServers}
                      isLoading={serversLoading}
                      disabled={!selectedEnvironment || serversLoading}
                      placeholder={resolvedServers.length > 0 ? 'Select server / server group / cluster' : 'No servers found...'}
                    />
                    <SupportedRuntimesTooltipResolver/>
                  </div>
                  {this.renderServerFromGroup(resolvedServerByGroup)}
                </Form>
              </div>
              <DownloadAmInstruction downloadInstaller={this.downloadInstaller}/>
              <div className={'hybrid-settings-configure-server'} data-test-id="hybrid-settings-configure-server">
                <span>3. Extract the zip file, which will create a new folder called am</span>
              </div>

              <div className={'hybrid-settings-configure-server'} data-test-id="hybrid-settings-configure-server">
                <span>4. cd into the am folder and run:</span>
                <span className={'command-option'}>On Linux/Unix</span>
                <span className={'command-markdown'}>./bin/install</span>
                <span className={'command-option'}>On Windows Server</span>
                <span className={'command-markdown'}>bin\install.ps1</span>
              </div>

              <div className={'hybrid-settings-configure-server'} data-test-id="hybrid-settings-configure-server">
      <span>5. Restart the
        {selectedServerId ? (
          <a
            id={'server-link'}
            href={`/cloudhub/#/console/home/servers/server/${selectedServerId}/settings`}
            target="_blank"
          >
            server
          </a>
        ) : (
          <span> server</span>
        )}
    </span>
              </div>

              <div className={'hybrid-settings-configure-server'} data-test-id="hybrid-settings-configure-server">
                <span>6. Finally, cd into am folder again and run:</span>
                <span className={'command-option'}>On Linux/Unix</span>
                <span className={'command-markdown'}>./bin/setup</span>
                <span className={'command-option'}>On Windows Server</span>
                <span className={'command-markdown'}>bin\setup.ps1</span>
              </div>

              <div
                className={'hybrid-settings-configure-server hybrid-settings-configure-server-last'}
                data-test-id="hybrid-settings-configure-server hybrid-settings-configure-server-last"
              >
                <span>In 5 minutes, you should see metrics appear in Anypoint Monitoring</span>
              </div>

            </div>
          </div>
        </div>
      </div>
    )
  }
}
